<template>
  <wb-card ref="sliderItem" :variant="variant" :data-theme="brand">
    <article class="owc-simple-teaser-item">
      <ValidationBoxWrapper v-if="isEditMode">
        <slot slot="validation-box" name="validation-box" />
      </ValidationBoxWrapper>
      <section v-if="image || hasTags" class="owc-simple-teaser-item__image-container">
        <ul v-if="hasTags" class="owc-simple-teaser-item__tags">
          <li v-for="(tag, index) in tags" :key="index" class="owc-simple-teaser-item__tag">
            <wb-tag :variant="getTagType(tag.type)">{{ tag.title }}</wb-tag>
          </li>
        </ul>
        <GenericImageComponent v-if="image" :imageData="image" />
      </section>
      <section class="owc-simple-teaser-item__content">
        <div class="owc-simple-teaser-item__content-container">
          <h2 class="owc-simple-teaser-item__headline">
            {{ headline }}
          </h2>
          <div v-if="copy" v-html="copy" class="owc-simple-teaser-item__description owc-rte-stylings" />
          <ul v-if="bullets" :class="bulletListClasses">
            <li
              class="owc-simple-teaser-item__bullet-item wb-list__item"
              v-for="(bullet, index) in bullets"
              :key="index"
            >
              {{ bullet }}
            </li>
          </ul>
        </div>
        <ul class="owc-simple-teaser-item__ctas" v-if="links">
          <li
            :class="{
              'owc-simple-teaser-item__ctas__item-link': !isCta(link.type),
              'owc-simple-teaser-item__ctas__item-button': isCta(link.type),
            }"
            v-for="(link, index) in links"
            :key="index"
          >
            <a :href="link.url" :target="link.target" :class="getCtaLinkClasses(link.type)">
              {{ link.label }}
            </a>
          </li>
        </ul>
      </section>
    </article>
  </wb-card>
</template>

<script lang="ts">
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import GenericImageGenerator from '@owc-utils/GenericImage/GenericImageGenerator';
import GenericImageComponent from '@owc-packages/GenericImage/src/GenericImage.vue';
import { SimpleTeaserItemComponent } from '@owc-packages/SimpleTeaser/src/SimpleTeaser';
import { TagType } from './SimpleTeaser.enums';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import Personalisation from '@owc-utils/Mixins/Personalisation';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import { setBrandTheme } from '@owc-utils/Theming/theming';

export default (ComponentInitializeDispatcher as SimpleTeaserItemComponent).extend({
  name: 'owc-simple-teaser-item',
  components: {
    GenericImageComponent,
    ValidationBoxWrapper,
  },
  mixins: [OwcDataComponent, Personalisation],
  computed: {
    tags: function() {
      return this.personaliseableData?.tags || [];
    },
    hasTags: function() {
      return Boolean(this.tags?.length);
    },
    links: function() {
      return this.personaliseableData?.links;
    },
    image: function() {
      const data = this.personaliseableData;
      if (data?.showMedia && data?.image) {
        return new GenericImageGenerator(
          this.personaliseableData.image,
          ['mq1', 'mq3', 'mq5'],
          '4x3', // this is the ratio that is defined on specs
        );
      }

      return null;
    },
    copy: function() {
      return this.personaliseableData?.copy;
    },
    bullets: function() {
      return this.personaliseableData?.bullets;
    },
    bulletListClasses: function() {
      return [
        'owc-simple-teaser-item__bullet-list',
        'wb-list',
        this.darkMode ? 'wb-list--theme-dark' : '',
        'wb-list--unordered',
      ];
    },
    headline: function() {
      return this.personaliseableData.headline;
    },
    darkMode: function() {
      return Boolean(this.personaliseableData.darkMode);
    },
    variant: function() {
      return this.darkMode ? 'dark-grey' : 'white';
    },
    brand() {
      return setBrandTheme();
    },
  },

  methods: {
    isCta(ctaType = '') {
      return Boolean(ctaType);
    },
    getCtaLinkClasses: function(ctaLinkType = '') {
      if (!this.isCta(ctaLinkType)) {
        return ['wb-link', this.darkMode ? 'wb-link--theme-dark' : '', 'wb-link--standalone'];
      }

      return [
        'owc-simple-teaser-item-button',
        'wb-button',
        this.darkMode ? 'wb-button--theme-dark' : '',
        `wb-button--${ctaLinkType}`,
        'wb-button--medium',
      ];
    },
    getTagType: function(tagType: number) {
      return TagType[tagType];
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/z-index-list';
@import '@owc-styles/utils/rtl-utils';
@import '@owc-styles/utils/rte';
@import '@owc-styles/utils/theming';

$simpleTeaserItemModuleName: 'owc-simple-teaser-item';

:host > wb-card {
  height: 100%;
  border: none;
}

.#{$simpleTeaserItemModuleName} {
  display: flex;
  flex-direction: column;
  height: 100%;

  .#{$simpleTeaserItemModuleName} {
    &__image-container {
      position: relative;

      min-height: 30px;
    }
    &__bullet-list {
      // 16px because it's the same margin between each bullet
      margin: 16px var(--wb-spacing-xxs) var(--wb-spacing-xxs);

      &-item {
        margin-top: var(--wb-spacing-3xs);

        &:first-child {
          margin-top: 0;
        }
      }
    }
    &__tags {
      position: absolute;
      top: 16px;
      left: var(--wb-spacing-s);
      z-index: $z-index-default;
    }
    &__tag {
      display: inline-block;
      margin-right: var(--wb-spacing-xxs);
    }
    &__content {
      display: flex;
      flex: 1;
      flex-direction: column;
      padding: var(--wb-spacing-xs) var(--wb-spacing-s) var(--wb-spacing-m);

      &-container {
        flex: 1;
      }
    }
    &__headline {
      @include wb-type-heading-xs;
    }
    &__description {
      @include wb-type-copy;

      margin-top: var(--wb-spacing-3xs);
    }
    &-button {
      width: 100%;

      text-align: left;
    }
    &__ctas {
      display: flex;
      flex-direction: column;
      margin-top: var(--wb-spacing-xs);

      &__item {
        &-link {
          margin: var(--wb-spacing-xxs) 0;

          &:last-child {
            margin-bottom: 0;
          }
        }
        &-button {
          margin-top: var(--wb-spacing-xxs);

          &:first-child {
            margin-top: 0;
          }
        }
      }
    }
  }
}

@include rtl-rule('.owc-simple-teaser-item', false) {
  &__content {
    text-align: right;
  }
  &-button {
    text-align: right;
  }
  &__tags {
    right: var(--wb-spacing-s);
    left: initial;
  }
  &__tag {
    margin-right: initial;
    margin-left: var(--wb-spacing-xxs);
  }
}
</style>
